/**
 * Dedicated header for Employer and Recruiter dashboards.
 * Uses unique class names (er-header-*) to avoid CSS conflicts with DashboardHeader.
 * Left section uses inline styles for guaranteed visibility.
 */

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faEnvelope, faSignOutAlt, faUserTie, faBriefcase, faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import './EmployerRecruiterHeader.css';

const EmployerRecruiterHeader = ({ userType = 'recruiter' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [profilePhotoPath, setProfilePhotoPath] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [imgError, setImgError] = useState(false);
  const notificationRef = useRef(null);
  const messageRef = useRef(null);
  const userMenuRef = useRef(null);

  const titleMap = { employer: 'EMPLOYER HUB', recruiter: 'RECRUITER HUB', jobSeeker: 'JOBSEEKER HUB', jobseeker: 'JOBSEEKER HUB', intern: 'INTERN HUB' };
  const title = titleMap[userType] || 'RECRUITER HUB';

  useEffect(() => {
    const fetchCounts = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const [notifRes, msgRes] = await Promise.all([
          axios.get(buildApiUrl('/api/notifications'), { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }),
          axios.get(buildApiUrl('/api/messages'), { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 })
        ]);
        if (notifRes.data?.success) {
          const n = notifRes.data.notifications || [];
          setNotifications(n);
          setNotificationCount(n.filter(x => !x.read).length);
        }
        if (msgRes.data?.success) {
          const m = msgRes.data.messages || [];
          setMessages(m);
          setMessageCount(m.filter(x => !x.read).length);
        }
      } catch (e) { /* ignore */ }
    };
    fetchCounts();
    const iv = setInterval(fetchCounts, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const isJobSeeker = ['jobSeeker', 'jobseeker'].includes(userType);
    const isIntern = userType === 'intern';
    const url = isIntern
      ? buildApiUrl('/api/intern/profile')
      : (isJobSeeker ? buildApiUrl('/api/jobseeker/profile') : buildApiUrl('/api/user/profile'));
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        const photo = data.profilePhotoPath || data.profilePhoto || data.profileImage || data.profilePicture || data.companyLogo;
        if (photo) setProfilePhotoPath(photo);
        if (!isJobSeeker && !isIntern) {
          setCompanyName(data.companyName || data.company_name || data.recruiterFullName || '');
        }
      })
      .catch(() => {});
  }, [userType]);

  useEffect(() => {
    const handleClick = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) setShowNotifications(false);
      if (messageRef.current && !messageRef.current.contains(e.target)) setShowMessages(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const getProfilePhotoUrl = (path) => {
    if (!path) return null;
    if (typeof path === 'string' && (path.startsWith('http://') || path.startsWith('https://'))) return path;
    const p = path.replace(/\\/g, '/');
    if (p.startsWith('uploads/intern_photos/')) return buildApiUrl(`/api/users/uploads/${p.replace('uploads/intern_photos/', 'intern_photos/')}`);
    if (p.includes('jobseeker_profiles/photos')) {
      const up = p.replace('uploads/jobseeker_profiles/photos/', '').replace('jobseeker_profiles/photos/', '');
      return buildApiUrl(`/api/users/uploads/jobseeker_profiles/photos/${up}`);
    }
    if (p.startsWith('uploads/profiles/')) return buildApiUrl(`/api/users/uploads/${p.replace('uploads/profiles/', 'profiles/')}`);
    if (p.startsWith('uploads/')) return buildApiUrl(`/api/users/uploads/${p.replace('uploads/', '')}`);
    if (p.startsWith('/uploads/')) return buildApiUrl(`/api/users/uploads/${p.replace('/uploads/', '')}`);
    return buildApiUrl(`/api/users/uploads/${p}`);
  };

  const getDisplayName = () => {
    const fn = user?.firstName || user?.first_name || '';
    const ln = user?.lastName || user?.last_name || '';
    const name = `${fn} ${ln}`.trim();
    if (name) return name;
    if (companyName) return companyName;
    return 'User';
  };

  const getInitials = () => {
    if (!user) return 'U';
    const fn = (user.firstName || user.first_name || '').charAt(0);
    const ln = (user.lastName || user.last_name || '').charAt(0);
    return `${fn}${ln}`.toUpperCase() || 'U';
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (e) {
      console.error(e);
    }
  };

  const photoUrl = user?.profileImage || user?.profilePhoto || user?.profilePhotoPath || user?.companyLogo || (profilePhotoPath ? getProfilePhotoUrl(profilePhotoPath) : null);
  const showPhoto = photoUrl && !imgError;

  return (
    <header className="er-header" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: 80,
      padding: '0 32px 0 24px',
      background: '#ffffff',
      borderBottom: '2px solid #e2e8f0',
      position: 'sticky',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1100,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
    }}>
      {/* LEFT: Logo + Title + Subtitle - all inline for guaranteed visibility */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
        minWidth: 220
      }}>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 8,
          background: 'linear-gradient(135deg, #ff6b35 0%, #14b8a6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          fontSize: 24
        }}>
          <FontAwesomeIcon
            icon={
              userType === 'intern'
                ? faGraduationCap
                : (userType === 'jobSeeker' || userType === 'jobseeker' ? faBriefcase : faUserTie)
            }
            style={{ color: '#ffffff' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
            {title}
          </span>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#334155', lineHeight: 1.2 }}>
            AksharJobs
          </span>
        </div>
      </div>

      {/* RIGHT: Notifications, Messages, User */}
      <div className="er-header-right">
        <div className="er-icon-wrap" ref={notificationRef}>
          <button
            type="button"
            className="er-icon-btn"
            onClick={() => { setShowNotifications(!showNotifications); setShowMessages(false); setShowUserMenu(false); }}
          >
            <FontAwesomeIcon icon={faBell} />
            {notificationCount > 0 && <span className="er-badge">{notificationCount}</span>}
          </button>
          {showNotifications && (
            <div className="er-dropdown">
              <div className="er-dropdown-head"><h4>Notifications</h4></div>
              <div className="er-dropdown-body">
                {notifications.length > 0 ? (
                  notifications.slice(0, 10).map((n, i) => (
                    <div key={n._id || i} className="er-dropdown-item" onClick={() => navigate('/notifications')}>
                      <p style={{ margin: 0, fontSize: 13, color: '#1e293b' }}>{n.message || n.title || 'Notification'}</p>
                    </div>
                  ))
                ) : (
                  <p style={{ margin: 0, padding: 16, color: '#94a3b8', fontSize: 13 }}>No new notifications</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="er-icon-wrap" ref={messageRef}>
          <button
            type="button"
            className="er-icon-btn"
            onClick={() => { setShowMessages(!showMessages); setShowNotifications(false); setShowUserMenu(false); }}
          >
            <FontAwesomeIcon icon={faEnvelope} />
            {messageCount > 0 && <span className="er-badge">{messageCount}</span>}
          </button>
          {showMessages && (
            <div className="er-dropdown">
              <div className="er-dropdown-head"><h4>Messages</h4></div>
              <div className="er-dropdown-body">
                {messages.length > 0 ? (
                  messages.slice(0, 10).map((m, i) => (
                    <div key={m._id || i} className="er-dropdown-item" onClick={() => navigate('/messages')}>
                      <p style={{ margin: 0, fontSize: 13, color: '#1e293b' }}>{m.sender_name || m.from_name || 'Message'}</p>
                    </div>
                  ))
                ) : (
                  <p style={{ margin: 0, padding: 16, color: '#94a3b8', fontSize: 13 }}>No new messages</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="er-user-wrap" ref={userMenuRef}>
          <div
            className="er-user-btn"
            onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifications(false); setShowMessages(false); }}
          >
            <span className="er-user-name">{getDisplayName()}</span>
            {showPhoto ? (
              <img src={photoUrl} alt="" className="er-avatar-img" onError={() => setImgError(true)} />
            ) : (
              <div className="er-avatar-initials">{getInitials()}</div>
            )}
          </div>
          {showUserMenu && (
            <div className="er-dropdown er-user-dropdown">
              <div className="er-dropdown-head" style={{ flexDirection: 'row', gap: 12 }}>
                <div>
                  <div className="er-dropdown-name">{getDisplayName()}</div>
                  <div className="er-dropdown-email">{user?.email}</div>
                </div>
                {showPhoto ? (
                  <img src={photoUrl} alt="" style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} onError={() => setImgError(true)} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, #ff6b35 0%, #14b8a6 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{getInitials()}</div>
                )}
              </div>
              <div className="er-dropdown-divider" />
              <div className="er-dropdown-item er-logout" onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} /> <span>Logout</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default EmployerRecruiterHeader;
